import { Txt } from 'components/Txt';
import { appendPunctuation, stringRemoveNewLines } from 'lib/riskbase';
import { theme } from 'lib/theme';
import * as React from 'react';
import { StyleSheet, View } from 'react-native';
import { AssessmentItemTypes } from 'store/assessments/types';

interface Props {
  description: string;
  superscript?: string;
  subscript?: string;
  type: AssessmentItemTypes.ACTION | AssessmentItemTypes.CONTROL;
  why: string;
  chips?: React.ReactNode;
  isUnreviewed?: boolean;
  cancelled?: boolean;
  size?: 'small' | 'normal';
  isAddCustom?: boolean;
  isSuggestion?: boolean;
  countSuggestions?: number;
  descriptionNumberOfLines?: number;
}

export const Finding: React.FC<Props> = ({
  chips,
  superscript,
  subscript,
  description,
  isUnreviewed = false,
  type,
  why,
  cancelled,
  size,
  isSuggestion,
  countSuggestions,
  descriptionNumberOfLines,
  isAddCustom,
}) => {
  const trimmedDescription = stringRemoveNewLines(description);
  const trimmedWhy = stringRemoveNewLines(why);

  return (
    <View style={{ position: 'relative' }}>
      <View style={[styles.view, isUnreviewed ? styles.viewUnreviewed : null]}>
        <View
          style={[
            size === 'small' ? styles.lineSmall : styles.line,
            {
              backgroundColor:
                type === AssessmentItemTypes.ACTION
                  ? theme.colors.assessmentAction
                  : theme.colors.assessmentControl,
            },
          ]}
        />
        <View style={styles.viewInner}>
          {!!superscript && (
            <Txt
              size="xxxsmall"
              weight="bold"
              style={{
                marginBottom:
                  size === 'small'
                    ? theme.paddingHorizontalHalf / 2
                    : theme.paddingHorizontalHalf,
                // color: theme.colors.fieldValueText,
              }}
            >
              {superscript}
            </Txt>
          )}
          <Txt
            numberOfLines={descriptionNumberOfLines}
            size={size === 'small' ? 'xsmall' : undefined}
          >
            {/* <Txt size={size === 'small' ? 'xxxsmall' : 'xxsmall'} weight="bold">
            Suggestion • 1 Added
          </Txt>{' '} */}
            {/**
             * "color: theme.colors.text" is required because of an Android bug.
             * Without it, when a description changes from an empty string to something,
             * the text is transparent and cannot be seen
             */}
            {trimmedWhy ? (
              <Txt
                style={{
                  color: trimmedDescription
                    ? theme.colors.text
                    : theme.colors.red,
                  textDecorationLine: cancelled ? 'line-through' : undefined,
                  textDecorationColor: cancelled
                    ? theme.colors.red50
                    : undefined,
                }}
                size={size === 'small' ? 'xsmall' : undefined}
              >
                {appendPunctuation(trimmedDescription || 'No Description')}{' '}
                <Txt
                  style={{
                    color: theme.colors.textBlack64,
                    textDecorationLine: cancelled ? 'line-through' : undefined,
                    textDecorationColor: cancelled
                      ? theme.colors.red50
                      : undefined,
                  }}
                  size={size === 'small' ? 'xsmall' : undefined}
                >
                  {trimmedWhy}
                </Txt>
              </Txt>
            ) : (
              <Txt
                style={{
                  color: trimmedDescription
                    ? theme.colors.text
                    : theme.colors.red,
                  textDecorationLine: cancelled ? 'line-through' : undefined,
                  textDecorationColor: cancelled
                    ? theme.colors.red50
                    : undefined,
                }}
                size={size === 'small' ? 'xsmall' : undefined}
              >
                {trimmedDescription || 'No Description'}
              </Txt>
            )}
            {/* {!!trimmedWhy && (
            <Txt
              style={[
                styles.textSecondary,
                {
                  textDecorationLine: cancelled ? 'line-through' : undefined,
                  textDecorationColor: cancelled
                    ? theme.colors.red50
                    : undefined,
                },
              ]}
              size={size === 'small' ? 'xsmall' : undefined}
            >
              &nbsp;&bull;&nbsp;
              {trimmedWhy}
            </Txt>
          )} */}
          </Txt>
          {!!subscript && (
            <Txt
              size="xxxsmall"
              weight="bold"
              style={{
                marginTop:
                  size === 'small'
                    ? theme.paddingHorizontalHalf / 2
                    : theme.paddingHorizontalHalf,
              }}
            >
              {subscript}
            </Txt>
          )}
          {!!chips && (
            <View
              style={{
                alignItems: 'center',
                flexDirection: 'row',
                flexWrap: 'wrap',
                marginTop:
                  (size === 'small'
                    ? theme.paddingHorizontalHalf / 2
                    : theme.paddingHorizontalHalf) - 6,
              }}
            >
              {chips}
            </View>
          )}
          {isAddCustom && (
            <View style={{ marginTop: 3 }}>
              <Txt size="xxxsmall">
                <Txt
                  size="xxxsmall"
                  style={{ color: theme.colors.textBlack64 }}
                  numberOfLines={1}
                >
                  Custom
                </Txt>
              </Txt>
            </View>
          )}
          {isSuggestion && (
            <View style={{ marginTop: 3 }}>
              <Txt size="xxxsmall">
                <Txt
                  size="xxxsmall"
                  style={{ color: theme.colors.textBlack64 }}
                  numberOfLines={1}
                >
                  Suggestion{' '}
                </Txt>
                <Txt
                  size="xxxsmall"
                  style={{
                    color: countSuggestions
                      ? theme.colors.textBlack64
                      : theme.colors.placeholder,
                  }}
                  weight="bold"
                >
                  {countSuggestions || 0} Added
                </Txt>
              </Txt>
            </View>
          )}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  line: {
    top: -theme.paddingVertical / 4,
    bottom: -theme.paddingVertical / 4,
    left: -5,
    borderRadius: 3,
    opacity: 0.5,
    position: 'absolute',
    width: 6,
  },
  lineSmall: {
    top: 0,
    bottom: 0,
    left: -5,
    borderRadius: 3,
    opacity: 0.5,
    position: 'absolute',
    width: 6,
  },
  view: {
    alignItems: 'center',
    flexDirection: 'row',
    minHeight: 48 - (theme.paddingVertical / 2) * 2,
    paddingLeft: 10,
  },
  viewInner: {
    flex: 1,
  },
  viewUnreviewed: {
    opacity: 0.42,
  },
});
